import { useEffect, useMemo, useState } from 'react';
import { isQuarantineTank } from '../../../shared/tank-purpose.js';
import { api } from '../api.js';
import { claimRequest, settleRequest, unsureSaveWords } from '../client-ref.js';
import { effectiveParams } from '../params.js';
import { loggedAtWords } from '../dose-input.js';
import { DOSE_LOG_UNITS, doseLogUnitLabel } from '../../../shared/dose-log.js';

// Log a dose of anything (feedback #60): iodine, trace elements, amino
// acids, nitrate or phosphate dosing, bacteria, another brand of
// alkalinity. TankSensei only records it: no math, and never a suggested
// amount; the product label is the authority.

function localNow() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// The time on screen goes with the save; the server keeps a saved dose's
// own time when the same save comes again, so a retry from a sheet opened
// later cannot move it after a test it was given before (the review of
// nightly sweep 2, 2026-10-24).
export function doseLogBody({ label, amount, unit, param, when, note }) {
  const t = new Date(when);
  return {
    product_label: label,
    amount,
    unit,
    param,
    ts: Number.isFinite(t.getTime()) ? t.toISOString() : undefined,
    note,
  };
}

// Which saves are the same dose: the same product, amount, unit and
// parameter, and the same time when the keeper set one, so yesterday's dose
// logged late is never taken for today's.
export function doseLogKey({ label, amount, unit, param, when, whenTouched }) {
  return JSON.stringify([
    String(label || '')
      .trim()
      .toLowerCase(),
    String(amount).trim(),
    unit,
    param,
    whenTouched ? when : '',
  ]);
}

export function DoseLogSheet({ tank, recent, onClose, onDone, onOpenCalculator = null }) {
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState('ml');
  const [param, setParam] = useState('other');
  const [when, setWhen] = useState(localNow);
  const [whenTouched, setWhenTouched] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');

  const params = useMemo(() => effectiveParams(tank), [tank]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  // The products this keeper logged by hand before, newest first, so the
  // daily dose is one tap.
  const again = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const r of recent || []) {
      if (r.method !== 'log' || !r.product_label) continue;
      const k = r.product_label.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(r);
      if (out.length >= 6) break;
    }
    return out;
  }, [recent]);

  const fillFrom = (r) => {
    setLabel(r.product_label);
    setAmount(r.amount != null ? String(Number(r.amount)) : '');
    setUnit(r.unit || 'ml');
    setParam(r.param || 'other');
  };

  const save = async () => {
    setBusy(true);
    setErr('');
    setNotice('');
    const body = doseLogBody({ label, amount, unit, param, when, note });
    // One save keeps its id until the server answers it, across a closed
    // sheet too, so a note added on the retry makes no twin (the server
    // takes the new note).
    const scope = `doselog:${tank.id}`;
    const ref = claimRequest(scope, doseLogKey({ label, amount, unit, param, when, whenTouched }));
    let saved;
    try {
      saved = await api(`/api/tanks/${tank.id}/dose-events`, {
        method: 'POST',
        body: { ...body, client_ref: ref },
      });
    } catch (e) {
      if (e.payload?.error === 'client_ref_mismatch') {
        settleRequest(scope);
        setErr(
          'A different dose was already logged from this sheet. Check Recent doses, then save this one again if you gave it.'
        );
      } else {
        setErr(unsureSaveWords(e) || e.message || 'Could not save.');
      }
      setBusy(false);
      return;
    }
    settleRequest(scope);
    // Saved. A refresh that fails must not read as a failed save: a second
    // tap would log the dose twice.
    try {
      await onDone?.();
    } catch {
      /* the tank reloads on its next visit */
    }
    setBusy(false);
    // The first try had gone through: say so, rather than close as if this
    // tap had added it.
    if (saved?.event?.duplicate) {
      const at = loggedAtWords(saved.event.ts);
      setNotice(`Already in your log${at ? ` from ${at}` : ''}. It is in your log once.`);
      return;
    }
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Log a dose"
        className="slide-in dl-sheet"
        style={{
          width: '100%',
          maxWidth: 448,
          maxHeight: '92vh',
          overflowY: 'auto',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          padding: '18px 22px max(28px, calc(env(safe-area-inset-bottom) + 16px))',
          background: 'var(--sheet-bg)',
          borderTop: '1px solid var(--surface-border)',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3
            className="font-heading"
            style={{ fontSize: 15, letterSpacing: '0.15em', textTransform: 'uppercase', margin: 0 }}
          >
            Log a dose
          </h3>
          <button type="button" className="ts-chip" onClick={onClose}>
            Close
          </button>
        </div>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-dim)', lineHeight: 1.45 }}>
          Any product, as you dosed it. We keep the record; the amount is yours and the
          label&rsquo;s.
        </p>
        {/* From Log: "how much alk?" is the calculator, not this record. */}
        {onOpenCalculator && (
          <button
            type="button"
            className="ts-chip"
            style={{ alignSelf: 'flex-start', minHeight: 44, padding: '0 14px' }}
            onClick={onOpenCalculator}
          >
            Work out the amount (dose calculator)
          </button>
        )}
        {isQuarantineTank(tank) && (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-dim)', lineHeight: 1.45 }}>
            Treating fish? Log the medication in its quarantine course (Tank, Quarantine and
            treatment), so it stays with each fish&rsquo;s history.
          </p>
        )}

        {again.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {again.map((r) => (
              <button
                key={r.id}
                type="button"
                className="ts-chip"
                style={{ minHeight: 44 }}
                onClick={() => fillFrom(r)}
              >
                {r.product_label} · {Number(r.amount)} {doseLogUnitLabel(r.unit)}
              </button>
            ))}
          </div>
        )}

        <label className="ts-field">
          <span>Product</span>
          <input
            type="text"
            value={label}
            maxLength={80}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="As on the bottle"
          />
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <label className="ts-field" style={{ flex: 1 }}>
            <span>Amount</span>
            <input
              type="number"
              step="any"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <label className="ts-field" style={{ flex: 1 }}>
            <span>Unit</span>
            <select className="dose-field" value={unit} onChange={(e) => setUnit(e.target.value)}>
              {DOSE_LOG_UNITS.map((u) => (
                <option key={u.k} value={u.k}>
                  {u.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="ts-field">
          <span>What it adds (optional)</span>
          <select className="dose-field" value={param} onChange={(e) => setParam(e.target.value)}>
            <option value="other">Something else, or not sure</option>
            {params.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label className="ts-field">
          <span>When</span>
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => {
              setWhen(e.target.value);
              setWhenTouched(true);
            }}
          />
        </label>
        <label className="ts-field">
          <span>Note (optional)</span>
          <input
            type="text"
            value={note}
            maxLength={300}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        {err && (
          <div role="alert" className="ts-err">
            {err}
          </div>
        )}
        {notice && (
          <div role="status" style={{ fontSize: 15, color: 'var(--text)', lineHeight: 1.45 }}>
            {notice}
          </div>
        )}
        <button
          type="button"
          className="ts-save"
          disabled={busy}
          onClick={save}
          style={{ minHeight: 48 }}
        >
          {busy ? 'Saving…' : 'Save dose'}
        </button>
      </div>
    </div>
  );
}
